import React, { useCallback, useMemo, useReducer } from 'react';

import * as http from '../../http';
import {
  buildAuthorizedDeleteRequest,
  buildAuthorizedGetRequest,
  buildAuthorizedPostRequest,
} from '../../http/request-templates';
import { GraphContext } from './GraphContext';
import {
  GraphCustomFiltersKeys,
  GraphGetRequestFilters,
  GraphReport,
  GraphType,
  SavedGraphFilter,
  StatisticsGetRequest,
} from './GraphModels';

import { createGuid } from '../../Utils/guid';
import useAuth from '../Auth/useAuth';
import graphReducer, {
  ERR_CREATE_SAVED_FILTERS,
  ERR_GET_GRAPH_REPORT,
  ERR_GET_GRAPH_REPORT_COMPARE,
  ERR_GET_SAVED_FILTERS,
  ERR_REMOVE_SAVED_FILTER,
  graphReducerInitialState,
  RCV_CREATE_SAVED_FILTERS,
  RCV_GET_GRAPH_REPORT,
  RCV_GET_GRAPH_REPORT_COMPARE,
  RCV_GET_SAVED_FILTERS,
  RCV_REMOVE_SAVED_FILTER,
  REQ_CREATE_SAVED_FILTERS,
  REQ_GET_GRAPH_REPORT,
  REQ_GET_GRAPH_REPORT_COMPARE,
  REQ_GET_SAVED_FILTERS,
  REQ_REMOVE_SAVED_FILTER,
} from './GraphReducer';

const filtersToQueryParams = (filters: GraphGetRequestFilters | undefined) => {
  let s = '';
  Object.keys(filters || {}).forEach((key) => {
    if (filters?.[key as GraphCustomFiltersKeys]?.length) {
      s += `&${key}=${filters[key as GraphCustomFiltersKeys]?.join(',')}`;
    }
  });
  return s;
};

interface GraphProviderProps {
  children: React.ReactNode;
}

const GraphProvider = ({ children }: GraphProviderProps) => {
  const { getAccessToken } = useAuth();

  const [graphState, dispatch] = useReducer(graphReducer, graphReducerInitialState);

  const getGraphReport = useCallback(
    async (graph: GraphType, settings: StatisticsGetRequest, skipCache: boolean = false) => {
      try {
        dispatch({ type: REQ_GET_GRAPH_REPORT });
        let url = `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/reports/graphs/${graph}?date_from=${
          settings.date_from
        }&date_to=${settings.date_to}${filtersToQueryParams(settings.filters)}`;

        if (graph == 'co2-savings') {
          url = `${
            import.meta.env.VITE_CUSTOMER_PORTAL_API
          }/reports/graphs/waste-by-month?value_field=co2saving&date_from=${
            settings.date_from
          }&date_to=${settings.date_to}${filtersToQueryParams(settings.filters)}`;
        }

        const response = await http.rawJson<GraphReport>(
          url,
          buildAuthorizedGetRequest(await getAccessToken()),
          { cache: !skipCache }
        );

        dispatch({ type: RCV_GET_GRAPH_REPORT, graphReport: response });
      } catch (e) {
        dispatch({ type: ERR_GET_GRAPH_REPORT });
      }
    },
    [getAccessToken]
  );

  const getGraphReportCompare = useCallback(
    async (graph: GraphType, settings: StatisticsGetRequest) => {
      try {
        dispatch({ type: REQ_GET_GRAPH_REPORT_COMPARE });
        let url = `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/reports/graphs/${graph}?date_from=${
          settings.date_from
        }&date_to=${settings.date_to}${filtersToQueryParams(settings.filters)}`;

        if (graph == 'co2-savings') {
          url = `${
            import.meta.env.VITE_CUSTOMER_PORTAL_API
          }/reports/graphs/waste-by-month?value_field=co2saving&date_from=${
            settings.date_from
          }&date_to=${settings.date_to}${filtersToQueryParams(settings.filters)}`;
        }

        const response = await http.rawJson<GraphReport>(
          url,
          buildAuthorizedGetRequest(await getAccessToken()),
          { cache: true }
        );

        dispatch({ type: RCV_GET_GRAPH_REPORT_COMPARE, graphReport: response });
      } catch (e) {
        dispatch({ type: ERR_GET_GRAPH_REPORT_COMPARE });
      }
    },
    [getAccessToken]
  );

  const getSavedFilters = useCallback(async () => {
    try {
      dispatch({ type: REQ_GET_SAVED_FILTERS });
      const url = `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/templates/materials-by-month`;
      const response = await http.rawJson<SavedGraphFilter[]>(
        url,
        buildAuthorizedGetRequest(await getAccessToken())
      );
      dispatch({ type: RCV_GET_SAVED_FILTERS, savedFilters: response });
    } catch (e) {
      dispatch({ type: ERR_GET_SAVED_FILTERS });
    }
  }, [getAccessToken]);

  const createSavedFilter = useCallback(
    async (newSavedFilter: SavedGraphFilter) => {
      try {
        dispatch({ type: REQ_CREATE_SAVED_FILTERS });
        const payload = { ...newSavedFilter, id: createGuid() };
        const url = `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/templates/materials-by-month`;
        await http.rawJson<SavedGraphFilter[]>(
          url,
          buildAuthorizedPostRequest(await getAccessToken(), payload)
        );
        dispatch({ type: RCV_CREATE_SAVED_FILTERS, savedFilter: payload });
      } catch (e) {
        dispatch({ type: ERR_CREATE_SAVED_FILTERS });
      }
    },
    [getAccessToken]
  );

  const removeSavedFilter = useCallback(
    async (id: string) => {
      try {
        dispatch({ type: REQ_REMOVE_SAVED_FILTER });
        await http.rawJson<SavedGraphFilter[]>(
          `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/templates/materials-by-month/${id}`,
          buildAuthorizedDeleteRequest(await getAccessToken())
        );
        dispatch({ type: RCV_REMOVE_SAVED_FILTER, id });
      } catch (e) {
        dispatch({ type: ERR_REMOVE_SAVED_FILTER });
      }
    },
    [getAccessToken]
  );

  const value = useMemo(
    () => ({
      ...graphState,
      getGraphReport,
      getGraphReportCompare,
      getSavedFilters,
      createSavedFilter,
      removeSavedFilter,
    }),
    [
      createSavedFilter,
      getGraphReport,
      getGraphReportCompare,
      getSavedFilters,
      graphState,
      removeSavedFilter,
    ]
  );

  return <GraphContext.Provider value={value}>{children}</GraphContext.Provider>;
};

export default GraphProvider;
